
  import cloneDeep from 'lodash/cloneDeep';
  import { validate } from '@/entities/checklistNotifiable';
  import { email } from '@/entities/validation';
  import { CheckboxField } from '@/ui/components/form/CheckboxField';
  import { SwitchField } from '@/ui/components/form/SwitchField';
  import { SEND_REPORT_BY_EMAIL_MODAL_WIDTH } from '@/ui/components/modals/constants';
  import { PageSubtitle } from '@/ui/components/PageSubtitle';
  import { ChooseUserField } from '@/ui/components/user/ChooseUserField';
  import { useValidation } from '@/ui/concerns';
  import { ChecklistNotifiableEditable, emptyScoreTrigger, ScoreTriggerEditable } from '@/ui/DTO';
  import { SelectOptions } from '@/ui/types';
  import { computed, defineComponent, ref } from 'vue';
  import { Modal } from '@/ui/components/Modal';
  import { Button } from '@/ui/components/Button';
  import { Field } from '@/ui/components/form/Field';
  // @ts-ignore
  import { BTaginput } from 'buefy/dist/esm/taginput';
  import ScoreTriggerOptions from '@/ui/components/issueTemplate/issueCreateTemplateModal/ScoreTriggerOptions.vue';

  interface ChecklistNotifiableModalProps {
    active: boolean;
    users: SelectOptions<number>;
    notifiable: ChecklistNotifiableEditable;
  }

  export default defineComponent<ChecklistNotifiableModalProps>({
    components: {
      SwitchField,
      CheckboxField,
      ChooseUserField,
      PageSubtitle,
      Modal,
      Button,
      Field,
      BTaginput,
      ScoreTriggerOptions,
    },
    props: ['active', 'users', 'notifiable'],
    setup(props, ctx) {
      const localNotifiable = ref<ChecklistNotifiableEditable>(cloneDeep(props.notifiable));

      const {
        validationErrors,
        hasNoValidationErrors,
        showValidationErrors,
      } = useValidation<ChecklistNotifiableEditable>(validate, localNotifiable);

      const missingUsersError = computed(() => validationErrors.value.some(
        error => error.field === 'users' && error.type === 'required',
      ));

      const closeModal = () => {
        ctx.emit('close');
      };

      const save = () => {
        showValidationErrors.value = true;

        if (hasNoValidationErrors.value) {
          ctx.emit('save', localNotifiable.value);
          showValidationErrors.value = false;
        }
      };

      const currentEmailTyping = ref('');
      const emailErrors = computed(() => currentEmailTyping.value ? email(currentEmailTyping.value, 'email') : []);
      const isValidEmail = computed(() => emailErrors.value.length < 1);

      const onTagInput = (tag: string[]) => {
        localNotifiable.value.externalAssociates = tag;
      };

      const onTyping = (value: string) => {
        currentEmailTyping.value = value;
      };

      const onAddOutHouseClick = () => {
        if (!isValidEmail.value) {
          return;
        }

        currentEmailTyping.value = '';
      };

      const onInHouseInputs = (value: number[]) => {
        localNotifiable.value.users = value;
      };

      const onAddNewUser = (value: number) => {
        localNotifiable.value.users.push(value);
      };

      const updateBoolean = (v: boolean) => {
        localNotifiable.value.entityItemSpecificAssignments = v;
      };

      const toggleScoreTrigger = (value: boolean) => {
        if (value) {
          localNotifiable.value.scoreTrigger = emptyScoreTrigger();
        } else {
          localNotifiable.value.scoreTrigger = null;
        }
      };

      const updateScoreTrigger = (value: ScoreTriggerEditable) => {
        localNotifiable.value.scoreTrigger = value;
      };

      return {
        width: SEND_REPORT_BY_EMAIL_MODAL_WIDTH,
        closeModal,
        save,
        emailErrors,
        onTagInput,
        onTyping,
        onInHouseInputs,
        onAddOutHouseClick,
        updateBoolean,
        toggleScoreTrigger,
        updateScoreTrigger,
        validationErrors,
        missingUsersError,
        localNotifiable,
        onAddNewUser,
      };
    },
  });
